import { lazy, Suspense, useEffect, useState } from "react";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@weblinkpong/ui";
import { SidebarInset, SidebarProvider } from "./components/ui/sidebar";
import { Skeleton } from "./components/ui/skeleton";
import {
  Activity,
  AlertTriangle,
  ArrowDownRight,
  Bell,
  Blocks,
  BookOpen,
  Box,
  BriefcaseBusiness,
  CalendarClock,
  CircleDollarSign,
  Command,
  DatabaseBackup,
  FileText,
  Gauge,
  Gift,
  Globe2,
  HandCoins,
  Images,
  LayoutDashboard,
  LifeBuoy,
  KeyRound,
  MessageSquareText,
  PackageOpen,
  RefreshCw,
  Search,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Tickets,
  UsersRound,
  Wrench,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  ApiError,
  getCurrentAdmin,
  getOverview,
  logoutAdmin,
  type AdminOverview,
  type AdminUser,
} from "./api";
import { AdminLogin } from "./login";
import type { CommercePageKey } from "./commerce-page";
import type { OperationsPageKey } from "./operations-page";
import type { PlatformPageKey } from "./platform-page";
import { AppSidebar } from "./components/layout/app-sidebar";
import { Header } from "./components/layout/header";
import { Main } from "./components/layout/main";
import type { AdminNavGroup } from "./components/layout/types";
import { SkipToMain } from "./components/skip-to-main";
import { ThemeSwitch } from "./components/theme-switch";
import { useAdminToasts } from "./hooks/use-admin-toasts";

const CommercePage = lazy(async () => ({
  default: (await import("./commerce-page")).CommercePage,
}));
const OperationsPage = lazy(async () => ({
  default: (await import("./operations-page")).OperationsPage,
}));
const PlatformPage = lazy(async () => ({
  default: (await import("./platform-page")).PlatformPage,
}));

const groups = [
  {
    label: "Customer delivery",
    items: [
      [LayoutDashboard, "Overview"],
      [UsersRound, "Customers"],
      [BriefcaseBusiness, "Projects"],
      [MessageSquareText, "Revisions"],
      [Tickets, "Tickets"],
      [UsersRound, "Customer form inbox"],
      [CalendarClock, "Renewals"],
    ],
  },
  {
    label: "Commerce",
    items: [
      [ShoppingCart, "Orders"],
      [PackageOpen, "Packages"],
      [Blocks, "Add-ons"],
      [FileText, "Quotes & invoices"],
      [CircleDollarSign, "Payments"],
      [Gift, "Gifts"],
      [Globe2, "Domains"],
    ],
  },
  {
    label: "Sales & content",
    items: [
      [UsersRound, "Sales enquiries"],
      [Images, "Portfolio"],
      [BookOpen, "Blog"],
      [FileText, "Legal pages"],
      [Wrench, "Tools policy"],
    ],
  },
  {
    label: "Affiliate program",
    items: [[HandCoins, "Affiliate partners"]],
  },
  {
    label: "Platform",
    items: [
      [KeyRound, "Integrations"],
      [ShieldCheck, "Staff & access"],
      [Bell, "Notifications"],
      [AlertTriangle, "Incidents"],
      [DatabaseBackup, "Backups"],
      [Activity, "Audit log"],
      [Settings, "Settings"],
    ],
  },
] as const;

type NavItem = readonly [LucideIcon, string];
function groupItems(group: (typeof groups)[number]): readonly NavItem[] {
  return group.items;
}

const commercePageByLabel: Readonly<Record<string, CommercePageKey>> = {
  Orders: "orders",
  Customers: "customers",
  Projects: "projects",
  Payments: "payments",
  Packages: "packages",
  "Add-ons": "add-ons",
};

const operationsPageByLabel: Readonly<Record<string, OperationsPageKey>> = {
  Revisions: "revisions",
  Tickets: "tickets",
  "Customer form inbox": "client-leads",
  Renewals: "renewals",
  "Quotes & invoices": "quotes-invoices",
  Gifts: "gifts",
  Domains: "domains",
  "Affiliate partners": "affiliates",
  "Sales enquiries": "sales-leads",
  Blog: "blog",
  "Legal pages": "legal-pages",
  Portfolio: "portfolio",
  "Tools policy": "tools-policy",
  Notifications: "notifications",
  Incidents: "incidents",
  Backups: "backups",
  "Audit log": "audit-log",
};

const platformPageByLabel: Readonly<Record<string, PlatformPageKey>> = {
  Integrations: "integrations",
  "Staff & access": "staff-access",
  Settings: "settings",
};

type AdminPageKey =
  CommercePageKey | OperationsPageKey | PlatformPageKey | "overview";
const commercePageKeys = Object.values(
  commercePageByLabel,
) as readonly string[];
const operationsPageKeys = Object.values(
  operationsPageByLabel,
) as readonly string[];
const platformPageKeys = Object.values(
  platformPageByLabel,
) as readonly string[];

function pageFromHash(): AdminPageKey {
  const hash = window.location.hash.slice(1);
  return [
    ...commercePageKeys,
    ...operationsPageKeys,
    ...platformPageKeys,
  ].includes(hash)
    ? (hash as AdminPageKey)
    : "overview";
}

const emptyOverview: AdminOverview = {
  counts: {
    paidUnfulfilled: 0,
    overdueProjects: 0,
    unansweredTickets: 0,
    renewalsDue: 0,
  },
  queue: [],
  incidents: [],
  backup: { status: "UNKNOWN", lastSuccessAt: null },
};

const navPermission: Readonly<Record<string, string>> = {
  Orders: "orders.read",
  Customers: "orders.read",
  Projects: "services.read",
  Revisions: "services.read",
  Tickets: "tickets.read",
  "Customer form inbox": "leads.read",
  Renewals: "renewals.read",
  Packages: "catalog.read",
  "Add-ons": "catalog.read",
  "Quotes & invoices": "orders.read",
  Payments: "payments.read",
  Gifts: "orders.read",
  Domains: "domains.read",
  "Affiliate partners": "affiliates.read",
  "Sales enquiries": "leads.read",
  Portfolio: "content.read",
  Blog: "content.read",
  "Legal pages": "content.read",
  "Tools policy": "tools.read",
  Integrations: "integrations.read",
  "Staff & access": "staff.manage",
  Notifications: "incidents.read",
  Incidents: "incidents.read",
  Backups: "backups.read",
  "Audit log": "audit.read",
  Settings: "integrations.read",
};

export function App() {
  const [admin, setAdmin] = useState<AdminUser | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [activePage, setActivePage] = useState<AdminPageKey>(pageFromHash);
  const [commandOpen, setCommandOpen] = useState(false);
  const [commandQuery, setCommandQuery] = useState("");
  const [overview, setOverview] = useState(emptyOverview);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  useAdminToasts({ error }, "admin-shell");
  async function load(signal?: AbortSignal) {
    setLoading(true);
    setError("");
    try {
      setOverview(await getOverview(signal));
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) {
        setAdmin(null);
        return;
      }
      if (!(caught instanceof DOMException && caught.name === "AbortError")) {
        setError(caught instanceof Error ? caught.message : "API unavailable");
      }
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    const controller = new AbortController();
    void getCurrentAdmin(controller.signal)
      .then(setAdmin)
      .catch((caught: unknown) => {
        if (!(caught instanceof DOMException && caught.name === "AbortError")) {
          setError(
            caught instanceof Error ? caught.message : "API unavailable",
          );
        }
      })
      .finally(() => setAuthLoading(false));
    return () => controller.abort();
  }, []);
  useEffect(() => {
    const onHashChange = () => setActivePage(pageFromHash());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setCommandOpen((value) => !value);
      }
      if (event.key === "Escape") setCommandOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);
  useEffect(() => {
    if (!admin) return;
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [admin]);

  async function signOut() {
    if (!admin) return;
    try {
      await logoutAdmin(admin);
    } finally {
      setAdmin(null);
      setOverview(emptyOverview);
    }
  }

  if (authLoading) {
    return (
      <main className="grid min-h-svh place-items-center bg-background p-6 text-center">
        <div className="grid justify-items-center gap-3">
          <span className="grid size-12 place-items-center rounded-xl bg-primary text-lg font-bold text-primary-foreground shadow-sm">
            W
          </span>
          <strong className="text-base">
            Securing your operations console…
          </strong>
          <small className="text-sm text-muted-foreground">
            Verifying the staff session and access scope.
          </small>
        </div>
      </main>
    );
  }

  if (!admin) {
    return (
      <AdminLogin
        onSuccess={(signedInAdmin) => {
          setError("");
          setAdmin(signedInAdmin);
        }}
      />
    );
  }

  const initials = admin.displayName
    .split(/\s+/)
    .map((part) => part[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const availableNavItems = groups
    .flatMap((group) => groupItems(group))
    .filter(
      ([, label]) =>
        !navPermission[label] ||
        admin.permissions.includes(navPermission[label]),
    );
  const stats = [
    [
      ShoppingCart,
      "Paid, not fulfilled",
      overview.counts.paidUnfulfilled,
      "Needs action",
    ],
    [
      CalendarClock,
      "Overdue projects",
      overview.counts.overdueProjects,
      "Delivery risk",
    ],
    [
      LifeBuoy,
      "Unanswered tickets",
      overview.counts.unansweredTickets,
      "SLA watch",
    ],
    [RefreshCw, "Renewals due", overview.counts.renewalsDue, "Next 30 days"],
  ] as const;
  const health: readonly [LucideIcon, string, string][] = [
    [CircleDollarSign, "Payment callbacks", "Waiting for health data"],
    [Globe2, "Porkbun domain jobs", "Waiting for health data"],
    [
      DatabaseBackup,
      "Encrypted backups",
      overview.backup.lastSuccessAt
        ? `Last success ${overview.backup.lastSuccessAt}`
        : "No verified run loaded",
    ],
    [AlertTriangle, "Open incidents", `${overview.incidents.length} loaded`],
  ];
  const sidebarGroups: readonly AdminNavGroup[] = groups.map((group) => ({
    title: group.label,
    items: groupItems(group)
      .filter(
        ([, label]) =>
          !navPermission[label] ||
          admin.permissions.includes(navPermission[label]),
      )
      .map(([icon, title]) => {
        const destination =
          title === "Overview"
            ? "overview"
            : (commercePageByLabel[title] ??
              operationsPageByLabel[title] ??
              platformPageByLabel[title] ??
              title.toLowerCase().replaceAll(" ", "-"));
        const badge =
          title === "Orders"
            ? overview.counts.paidUnfulfilled
            : title === "Tickets"
              ? overview.counts.unansweredTickets
              : title === "Incidents"
                ? overview.incidents.length
                : undefined;
        const item = {
          title,
          url: `#${destination}`,
          icon,
          active: destination === activePage,
        };
        return badge === undefined ? item : { ...item, badge };
      }),
  }));
  return (
    <SidebarProvider>
      <SkipToMain />
      <AppSidebar
        groups={sidebarGroups}
        workspace={{
          name: "WebLinkPong BD",
          environment: "Production",
          currency: "BDT",
        }}
        user={{
          name: admin.displayName,
          email: admin.email,
          role: admin.role,
          initials,
        }}
        onNavigate={(destination) => {
          window.location.hash = destination;
        }}
        onSignOut={() => void signOut()}
      />
      <SidebarInset id="admin-main" className="@container/content min-h-svh">
        <Header>
          <Button
            variant="outline"
            className="h-9 w-full max-w-md justify-start text-muted-foreground shadow-none"
            onClick={() => setCommandOpen(true)}
          >
            <Search />
            <span className="hidden truncate sm:inline">
              Search orders, projects, customers…
            </span>
            <kbd className="ml-auto hidden rounded border bg-muted px-1.5 py-0.5 text-[10px] font-medium sm:inline-flex">
              <Command /> K
            </kbd>
          </Button>
          <div className="ml-auto flex items-center gap-2">
            <Button
              variant="outline"
              className="hidden sm:inline-flex"
              onClick={() => {
                window.location.hash = "incidents";
              }}
            >
              <Gauge />
              <span>System health</span>
              <i
                className={
                  error
                    ? "size-2 rounded-full bg-destructive"
                    : "size-2 rounded-full bg-emerald-500"
                }
              />
            </Button>
            <ThemeSwitch />
            <Button
              variant="outline"
              size="icon"
              aria-label="Notifications"
              onClick={() => {
                window.location.hash = "notifications";
              }}
            >
              <Bell />
            </Button>
          </div>
        </Header>
        <Suspense
          fallback={
            <div className="flex min-h-[calc(100svh-4rem)] items-center justify-center gap-3">
              <Skeleton className="h-8 w-8 rounded-full" />
              <Skeleton className="h-4 w-56" />
            </div>
          }
        >
          {commercePageKeys.includes(activePage) ? (
            <CommercePage
              key={`commerce-${activePage}`}
              page={activePage as CommercePageKey}
              admin={admin}
              onUnauthorized={() => setAdmin(null)}
            />
          ) : operationsPageKeys.includes(activePage) ? (
            <OperationsPage
              key={`operations-${activePage}`}
              page={activePage as OperationsPageKey}
              admin={admin}
              onUnauthorized={() => setAdmin(null)}
            />
          ) : platformPageKeys.includes(activePage) ? (
            <PlatformPage
              key={`platform-${activePage}`}
              page={activePage as PlatformPageKey}
              admin={admin}
              onUnauthorized={() => setAdmin(null)}
            />
          ) : (
            <Main>
              <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
                <div>
                  <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Sunday, 16 August 2026
                  </span>
                  <h1 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">
                    Operations overview
                  </h1>
                  <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                    What needs attention across sales, fulfilment, support,
                    renewals, and platform health.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={() => void load()}
                    disabled={loading}
                  >
                    <RefreshCw className={loading ? "animate-spin" : ""} />{" "}
                    Refresh
                  </Button>
                  <Button
                    onClick={() => {
                      window.location.hash = "quotes-invoices";
                    }}
                  >
                    <Box /> Create quote
                  </Button>
                </div>
              </div>
              {error && (
                <Alert
                  variant="destructive"
                  className="mb-5 flex items-start gap-3"
                >
                  <AlertTriangle />
                  <AlertTitle>{error}</AlertTitle>
                  <AlertDescription>
                    Mock data is intentionally disabled. Sign in to the owned
                    NestJS API or check the deployment health to load
                    operational data.
                  </AlertDescription>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void load()}
                  >
                    Try again
                  </Button>
                </Alert>
              )}
              <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
                {stats.map(([Icon, label, value, note], index) => (
                  <Card key={label} className="gap-3 py-5">
                    <CardHeader className="flex flex-row items-center gap-3 px-5">
                      <span
                        className={`grid size-9 place-items-center rounded-lg ${["bg-blue-100 text-blue-700", "bg-amber-100 text-amber-700", "bg-violet-100 text-violet-700", "bg-emerald-100 text-emerald-700"][index]}`}
                      >
                        <Icon />
                      </span>
                      <CardTitle className="text-sm font-medium text-muted-foreground">
                        {label}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="px-5">
                      <strong className="text-2xl font-bold tabular-nums">
                        {loading ? "—" : value}
                      </strong>
                      <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                        <ArrowDownRight /> {note}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </section>
              <div className="mt-4 grid gap-4 xl:grid-cols-[minmax(0,1.65fr)_minmax(280px,.75fr)]">
                <Card className="gap-0 overflow-hidden py-0">
                  <div className="flex items-start justify-between gap-4 border-b px-6 py-5">
                    <div>
                      <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        Priority queue
                      </span>
                      <h2 className="mt-1 text-lg font-semibold">
                        Work requiring action
                      </h2>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        window.location.hash = "orders";
                      }}
                    >
                      View all
                    </Button>
                  </div>
                  <div>
                    {overview.queue.length ? (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Item</TableHead>
                            <TableHead>Status</TableHead>
                            <TableHead>Age</TableHead>
                            <TableHead>Assignee</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {overview.queue.map((item) => (
                            <TableRow key={item.id}>
                              <TableCell>
                                <strong>{item.title}</strong>
                              </TableCell>
                              <TableCell>
                                <Badge variant="secondary">{item.status}</Badge>
                              </TableCell>
                              <TableCell>{item.age}</TableCell>
                              <TableCell>
                                {item.assignee ?? "Unassigned"}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    ) : (
                      <Empty className="m-6">
                        <EmptyHeader>
                          <EmptyMedia>
                            <ShieldCheck />
                          </EmptyMedia>
                          <EmptyTitle>
                            {loading
                              ? "Loading operational queue…"
                              : error
                                ? "Connect API to view queue"
                                : "No priority work"}
                          </EmptyTitle>
                          <EmptyDescription>
                            Paid orders, overdue work, unanswered tickets, and
                            renewal actions appear here.
                          </EmptyDescription>
                        </EmptyHeader>
                      </Empty>
                    )}
                  </div>
                </Card>
                <Card className="gap-0 overflow-hidden py-0">
                  <div className="flex items-start justify-between gap-4 border-b px-6 py-5">
                    <div>
                      <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                        Platform
                      </span>
                      <h2 className="mt-1 text-lg font-semibold">
                        Operational health
                      </h2>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Platform settings"
                      onClick={() => {
                        window.location.hash = "settings";
                      }}
                    >
                      <Settings />
                    </Button>
                  </div>
                  <div className="divide-y">
                    {health.map(([Icon, label, note]) => (
                      <article
                        className="flex items-center gap-3 px-5 py-4"
                        key={String(label)}
                      >
                        <span className="grid size-9 place-items-center rounded-lg bg-muted text-muted-foreground">
                          <Icon />
                        </span>
                        <div className="grid min-w-0 flex-1">
                          <strong className="text-sm font-medium">
                            {String(label)}
                          </strong>
                          <small className="truncate text-xs text-muted-foreground">
                            {String(note)}
                          </small>
                        </div>
                        <i
                          className={
                            error
                              ? "size-2 rounded-full bg-amber-500"
                              : "size-2 rounded-full bg-emerald-500"
                          }
                        />
                      </article>
                    ))}
                  </div>
                </Card>
              </div>
              <Card className="mt-4 flex flex-col items-start justify-between gap-4 p-5 sm:flex-row sm:items-center">
                <div className="flex items-start gap-3">
                  <span className="grid size-10 shrink-0 place-items-center rounded-lg bg-accent text-accent-foreground">
                    <ShieldCheck />
                  </span>
                  <div>
                    <strong className="text-sm font-semibold">
                      Admin uses owned auth and RBAC
                    </strong>
                    <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
                      Clerk, mock users, mock tasks, and static business data
                      from the upstream template are not included.
                      Owner/Admin/Sales/PM/Developer/Support/Finance permissions
                      resolve server-side with assignment scopes and audited
                      overrides.
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={() => {
                    window.location.hash = "staff-access";
                  }}
                >
                  Review access model
                </Button>
              </Card>
            </Main>
          )}
        </Suspense>
      </SidebarInset>
      <CommandDialog
        open={commandOpen}
        onOpenChange={setCommandOpen}
        title="Admin navigation"
        description="Jump to an admin section"
      >
        <CommandInput
          value={commandQuery}
          onValueChange={setCommandQuery}
          placeholder="Search orders, projects, customers…"
        />
        <CommandList>
          <CommandEmpty>No matching section.</CommandEmpty>
          <CommandGroup heading="Navigation">
            {availableNavItems.map(([Icon, label]) => {
              const destination =
                commercePageByLabel[label] ??
                operationsPageByLabel[label] ??
                platformPageByLabel[label] ??
                "overview";
              return (
                <CommandItem
                  key={label}
                  value={label}
                  onSelect={() => {
                    window.location.hash = destination;
                    setCommandOpen(false);
                    setCommandQuery("");
                  }}
                >
                  <Icon />
                  <span>{label}</span>
                  <Badge variant="outline" className="ml-auto">
                    {navPermission[label] ?? "overview"}
                  </Badge>
                </CommandItem>
              );
            })}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </SidebarProvider>
  );
}
